import {ReportDefinitionV1} from './contracts/report-definition.types';

/** The grains this builder can build.
 *
 * Deliberately narrower than the contract's `reportType` enum. The other three values name
 * reports that have their own pages and that the builder does not author, so they resolve to no
 * grain at all rather than being coerced into one. */
export type ReportGrain = 'TASK_REPORT' | 'PROJECT_STATUS';

/** The contract's own `reportType` values, with the optionality stripped. Anchoring the display
 * names to this rather than to `string` is what makes a mistyped display name a compile error. */
type ReportTypeDisplayName = NonNullable<ReportDefinitionV1['reportType']>;

/** Taxonomy token -> the definition `reportType` display string.
 *
 * Two vocabularies that are NOT derivable from each other. 'Project Status' -> 'PROJECT_STATUS'
 * happens to be a plain uppercase; 'Task report' -> 'TASK_REPORT' is not, and report-service's
 * `ReportType` carries an explicit comment that its casing inconsistency is load-bearing (the
 * display string is matched as a raw string against the stored `type` column). So this is a
 * literal map — never rebuild either side with toUpperCase()/toLowerCase().
 *
 * Typed to the contract's own union rather than `string`: report-service compares this value
 * raw against a stored column, so a well-meaning casing tidy-up here would compile, pass tsc and
 * pass these tests while silently writing a type no report is ever found by. Against the union,
 * the same typo is a TS2820 with the correct spelling suggested. */
const DISPLAY_NAME_BY_GRAIN: Record<ReportGrain, ReportTypeDisplayName> = {
	TASK_REPORT: 'Task report',
	PROJECT_STATUS: 'Project Status',
};

/** Field-tree root -> grain.
 *
 * report-service selects its resolver by matching the root field name against
 * `FieldResolver.getFieldEntityName()`, so the root is what actually decides which rows a
 * definition returns. That makes it the one grain signal that cannot be wrong, and the only
 * evidence a definition carries once its `reportType` has been stripped.
 *
 * A Map, not an object literal: field names come from an LLM and round-trip through storage
 * against a schema that constrains them to a bare string, so a field called `toString` or
 * `constructor` reaches this lookup. On an object literal that returns an inherited function —
 * truthy, so it would be returned as if it were a grain, and both consumers then fail silently
 * (a function does not survive JSON.stringify into the taxonomy query, and has no display name
 * at save). A Map only ever answers for keys actually put in it. */
const GRAIN_BY_FIELD_ROOT = new Map<string, ReportGrain>([
	['task', 'TASK_REPORT'],
	['taskTotals', 'TASK_REPORT'],
	['projectStatus', 'PROJECT_STATUS'],
]);

/** The `reportType` display string a grain is written as. Used at save, so a report is recorded
 * as the grain it was filtered as. */
export function displayNameOf(grain: ReportGrain): ReportTypeDisplayName {
	return DISPLAY_NAME_BY_GRAIN[grain];
}

/** Which grain a definition holds, by precedence.
 *
 * 1. An explicit `reportType` decides. One this builder does not build yields null, which gates
 *    the conditions editor instead of offering it the wrong grain's fields — the same
 *    fail-closed posture as `loadDefinition`'s viewerPermission narrowing.
 * 2. An absent `reportType` falls through to the field root. Absent is legitimate in two ways:
 *    every report predating the grain concept has none, and the drafting agent deliberately pops
 *    the field off a validation-failure fallback definition while leaving that grain's default
 *    columns in place.
 * 3. An unrecognised root, or no fields, defaults to task — the contract's established meaning
 *    of an absent reportType, and what `buildSaveReportAsInput` has always written.
 *
 * NOTE: this deliberately diverges from `forecast-ai-agents`' `grain.py`, which coerces an
 * unknown report type to TASK_REPORT. That is a producer choosing a value to emit, where an
 * invalid one kills the whole draft and there is no safe degraded state. This is a consumer
 * interpreting a value someone else chose, and it has one. Do not "fix" the inconsistency. */
export function grainOf(definition: Pick<ReportDefinitionV1, 'reportType' | 'fields'>): ReportGrain | null {
	const {reportType} = definition;
	if (reportType !== undefined && reportType !== null) {
		const grains = Object.keys(DISPLAY_NAME_BY_GRAIN) as ReportGrain[];
		return grains.find(grain => DISPLAY_NAME_BY_GRAIN[grain] === reportType) ?? null;
	}
	for (const field of definition.fields ?? []) {
		const grain = GRAIN_BY_FIELD_ROOT.get(field.name);
		if (grain) {
			return grain;
		}
	}
	return 'TASK_REPORT';
}
